import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { type VariantProps, cva } from 'class-variance-authority';
import { cn } from 'cn';

/** Wraps a subtree so its tooltips share hover/focus timing. */
const TooltipProvider = TooltipPrimitive.Provider;

/** Root that pairs a trigger with its content and owns open state. */
const Tooltip = TooltipPrimitive.Root;

/** Element the tooltip is anchored to; use `asChild` to keep your own markup. */
const TooltipTrigger = TooltipPrimitive.Trigger;

/** Content styling; `variant="small"` shrinks the body text to text-xs. */
const tooltipContentVariants = cva('bg-popover text-popover-foreground animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 overflow-hidden rounded-md border px-3 py-1.5 shadow-md', {
  variants: {
    variant: {
      default: 'text-sm',
      small: 'text-xs'
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});

/** Floating tooltip body, portaled out and themed with popover tokens. */
const TooltipContent = React.forwardRef<React.ElementRef<typeof TooltipPrimitive.Content>, React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & VariantProps<typeof tooltipContentVariants> & {
  container?: HTMLElement | null;
}>(({
  className,
  sideOffset = 4,
  variant,
  container,
  ...props
}, ref) => <TooltipPrimitive.Portal container={container}>
    <TooltipPrimitive.Content ref={ref} sideOffset={sideOffset} className={cn(tooltipContentVariants({
    variant
  }), className)} {...props} />
  </TooltipPrimitive.Portal>);
TooltipContent.displayName = TooltipPrimitive.Content.displayName;
interface TooltipWrapperProps {
  /** Tooltip body text. */
  content: React.ReactNode;
  /** Trigger element (rendered via `asChild`). */
  children: React.ReactNode;
  /** Side the tooltip appears on. */
  side?: React.ComponentProps<typeof TooltipContent>['side'];
  /** Alignment along the side; defers to TooltipContent's default when unset. */
  align?: React.ComponentProps<typeof TooltipContent>['align'];
  /** Content size variant; defers to TooltipContent's default when unset. */
  variant?: React.ComponentProps<typeof TooltipContent>['variant'];
  /** Extra classes on the tooltip body. */
  className?: string;
  /**
   * Set when the trigger is a disabled control. A disabled element fires no
   * pointer/focus events, so the tooltip would never open; this wraps it in a
   * focusable span that anchors the tooltip instead.
   */
  triggerDisabled?: boolean;
  /** Provider open delay in ms. */
  delayDuration?: React.ComponentProps<typeof TooltipProvider>['delayDuration'];
}

/**
 * All-in-one tooltip: bundles Provider + Root + Trigger + Content into a single
 * component so a tooltip is one tag wrapping its trigger, not a four-element
 * tree. Pass the trigger as children and the body as `content`. For a tooltip
 * that only appears under a condition, use ConditionalTooltip instead.
 */
function TooltipWrapper({
  content,
  children,
  side = 'top',
  align,
  variant,
  className,
  triggerDisabled = false,
  delayDuration = 150
}: TooltipWrapperProps) {
  return <TooltipProvider delayDuration={delayDuration} data-sentry-element="TooltipProvider" data-sentry-component="TooltipWrapper" data-sentry-source-file="tooltip.tsx">
      <Tooltip data-sentry-element="Tooltip" data-sentry-source-file="tooltip.tsx">
        <TooltipTrigger asChild data-sentry-element="TooltipTrigger" data-sentry-source-file="tooltip.tsx">
          {triggerDisabled ? <span className="inline-flex cursor-not-allowed">{children}</span> : children}
        </TooltipTrigger>
        <TooltipContent side={side} align={align} variant={variant} className={className} data-sentry-element="TooltipContent" data-sentry-source-file="tooltip.tsx">
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>;
}
export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, TooltipWrapper };